import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';

const MandateForm = () => {
  const [formData, setFormData] = useState({
    locations: '',
    budget: '',
    minSpace: '',
    propertyType: '',
    additionalNotes: '',
    exclusivity: 'exclusive',
    duration: '6',
    fees: '5',
    clientName: '',
    clientCompany: '',
    clientEmail: '',
    clientPhone: '',
    mandateDate: ''
  });

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const today = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    setFormData(prev => ({ ...prev, mandateDate: today }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.locations.trim()) newErrors.locations = 'Locations are required';
    if (!formData.budget.trim()) newErrors.budget = 'Budget is required';
    if (!formData.minSpace.trim()) newErrors.minSpace = 'Minimum space is required';
    if (!formData.propertyType.trim()) newErrors.propertyType = 'Property type is required';
    if (!formData.clientName.trim()) newErrors.clientName = 'Full name is required';
    if (!formData.clientEmail.trim()) newErrors.clientEmail = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.clientEmail)) newErrors.clientEmail = 'Invalid email format';
    if (!formData.clientPhone.trim()) newErrors.clientPhone = 'Phone is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowConfirmation(true);
    }
  };

  const generatePDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    
    // Print-friendly colors (black, white, grey)
    const black = [0, 0, 0];
    const darkGrey = [51, 51, 51];
    const mediumGrey = [128, 128, 128];
    const lightGrey = [200, 200, 200];
    const white = [255, 255, 255];
    
    // Load and add logo
    const logoUrl = 'https://customer-assets.emergentagent.com/job_elite-properties-18/artifacts/69sbmjqc_MaisonEze.png';
    
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = logoUrl;
      });
      
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const logoBase64 = canvas.toDataURL('image/png');
      
      // Logo centered at top - smaller size
      const logoWidth = 30;
      const logoHeight = (img.height / img.width) * logoWidth;
      doc.addImage(logoBase64, 'PNG', (pageWidth - logoWidth) / 2, 8, logoWidth, logoHeight);
    } catch (e) {
      // Fallback: Text logo
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...black);
      doc.text('MAISON EZE', pageWidth / 2, 18, { align: 'center' });
    }
    
    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    doc.text('PROPERTY SEARCH MANDATE', pageWidth / 2, 38, { align: 'center' });
    
    // Thin line under title
    doc.setDrawColor(...lightGrey);
    doc.setLineWidth(0.3);
    doc.line(margin, 42, pageWidth - margin, 42);
    
    // Date - right aligned, small
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...mediumGrey);
    doc.text(`Date: ${formData.mandateDate}`, pageWidth - margin, 48, { align: 'right' });
    
    let yPos = 55;
    
    // Helper function for section headers
    const addSectionHeader = (title, y) => {
      doc.setFillColor(...lightGrey);
      doc.rect(margin, y - 4, pageWidth - (margin * 2), 6, 'F');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...black);
      doc.text(title, margin + 2, y);
      return y + 8;
    };
    
    // Helper function for fields - compact
    const addField = (label, value, y) => {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkGrey);
      doc.text(label, margin + 2, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...black);
      const labelWidth = doc.getTextWidth(label) + 1;
      
      // Handle long text - truncate or wrap
      const maxWidth = pageWidth - margin - (margin + 2 + labelWidth) - 5;
      let displayValue = value || 'N/A';
      if (doc.getTextWidth(displayValue) > maxWidth) {
        while (doc.getTextWidth(displayValue + '...') > maxWidth && displayValue.length > 0) {
          displayValue = displayValue.slice(0, -1);
        }
        displayValue += '...';
      }
      doc.text(displayValue, margin + 2 + labelWidth, y);
      return y + 5;
    };
    
    // Section 1: Property Requirements
    yPos = addSectionHeader('1. PROPERTY REQUIREMENTS', yPos);
    yPos = addField('Locations:', formData.locations, yPos);
    yPos = addField('Budget:', formData.budget, yPos);
    yPos = addField('Min. Space:', formData.minSpace, yPos);
    yPos = addField('Property Type:', formData.propertyType, yPos);
    if (formData.additionalNotes) {
      yPos = addField('Notes:', formData.additionalNotes, yPos);
    }
    
    // Section 2: Mandate Terms
    yPos += 3;
    yPos = addSectionHeader('2. MANDATE TERMS', yPos);
    yPos = addField('Exclusivity:', formData.exclusivity === 'exclusive' ? 'Exclusive (no other agents)' : 'Non-exclusive', yPos);
    yPos = addField('Duration:', `${formData.duration} months`, yPos);
    yPos = addField('Fees:', formData.fees === 'negotiable' ? 'Negotiable (case-by-case)' : `${formData.fees}% of transaction`, yPos);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...mediumGrey);
    doc.text('Fees are due only upon successful transaction via Maison Eze.', margin + 2, yPos);
    yPos += 3;
    
    // Section 3: Client Details
    yPos += 3;
    yPos = addSectionHeader('3. CLIENT DETAILS', yPos);
    yPos = addField('Full Name:', formData.clientName, yPos);
    if (formData.clientCompany) {
      yPos = addField('Company:', formData.clientCompany, yPos);
    }
    yPos = addField('Email:', formData.clientEmail, yPos);
    yPos = addField('Phone:', formData.clientPhone, yPos);
    
    // Section 4: Maison Eze Representative
    yPos += 3;
    yPos = addSectionHeader('4. MAISON EZE REPRESENTATIVE', yPos);
    yPos = addField('Name:', 'Gregory Ezeguelian', yPos);
    yPos = addField('Email:', 'gregory@maison-eze.com', yPos);
    yPos = addField('Phone:', 'FR +33 6 21371677 / GR +30 699 3378555', yPos);
    
    // SIGNATURE SECTION - Dedicated area
    yPos += 10;
    
    // Signature section header
    doc.setDrawColor(...black);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    
    yPos += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    doc.text('SIGNATURES & VALIDATION', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 8;
    
    // Two signature boxes side by side
    const boxWidth = (pageWidth - (margin * 2) - 20) / 2;
    const boxHeight = 30;
    
    // Client signature box
    doc.setDrawColor(...darkGrey);
    doc.setLineWidth(0.3);
    doc.rect(margin, yPos, boxWidth, boxHeight);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...mediumGrey);
    doc.text('Client Signature:', margin + 2, yPos + 5);
    doc.text('Date:', margin + 2, yPos + boxHeight - 8);
    doc.line(margin + 12, yPos + boxHeight - 5, margin + boxWidth - 5, yPos + boxHeight - 5);
    
    // Maison Eze signature box
    const rightBoxX = margin + boxWidth + 20;
    doc.rect(rightBoxX, yPos, boxWidth, boxHeight);
    doc.text('Maison Eze Representative:', rightBoxX + 2, yPos + 5);
    doc.text('Date:', rightBoxX + 2, yPos + boxHeight - 8);
    doc.line(rightBoxX + 12, yPos + boxHeight - 5, rightBoxX + boxWidth - 5, yPos + boxHeight - 5);
    
    yPos += boxHeight + 5;
    
    // Stamp area
    doc.setFontSize(7);
    doc.setTextColor(...mediumGrey);
    doc.text('Company Stamp (if applicable):', margin, yPos + 3);
    
    // Stamp box
    doc.setDrawColor(...lightGrey);
    doc.setLineDashPattern([2, 1], 0);
    doc.rect(margin, yPos + 6, 40, 25);
    doc.setLineDashPattern([], 0);
    
    // Instructions box - prominent
    yPos += 35;
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, yPos, pageWidth - (margin * 2), 28, 'F');
    doc.setDrawColor(...darkGrey);
    doc.setLineWidth(0.3);
    doc.rect(margin, yPos, pageWidth - (margin * 2), 28);
    
    yPos += 6;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    doc.text('INSTRUCTIONS:', margin + 5, yPos);
    
    yPos += 5;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGrey);
    doc.text('1. Print this document', margin + 5, yPos);
    yPos += 4;
    doc.text('2. Sign and stamp (if applicable) in the designated areas above', margin + 5, yPos);
    yPos += 4;
    doc.text('3. Scan the signed document', margin + 5, yPos);
    yPos += 4;
    doc.setFont('helvetica', 'bold');
    doc.text('4. Email to: gregory@maison-eze.com', margin + 5, yPos);
    
    // Footer - minimal
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...mediumGrey);
    doc.text('Maison Eze | contact@maison-eze.com | www.maison-eze.com', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    // Save the PDF
    const fileName = `Mandate_MaisonEze_${formData.clientName.replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);
    
    return fileName;
  };

  const handleConfirm = async () => {
    const fileName = await generatePDF();
    toast.success(`PDF downloaded: ${fileName}`, {
      description: 'Please sign, stamp (if applicable), and email to gregory@maison-eze.com'
    });
    
    // Open email client
    const subject = encodeURIComponent('Signed Mandate - ' + formData.clientName);
    const body = encodeURIComponent(`Dear Maison Eze,\n\nPlease find attached my signed mandate.\n\nBest regards,\n${formData.clientName}`);
    window.open(`mailto:gregory@maison-eze.com?subject=${subject}&body=${body}`, '_blank');
    
    setShowConfirmation(false);
  };

  const getFeesLabel = (value) => {
    if (value === 'negotiable') return 'Negotiable (case-by-case)';
    return `${value}% of transaction`;
  };

  return (
    <div className="min-h-screen" style={{ background: '#fffef2' }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4" style={{ background: 'rgba(255,254,242,0.95)', borderBottom: '1px solid rgba(51,51,51,0.1)' }}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <a href="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_elite-properties-18/artifacts/69sbmjqc_MaisonEze.png" 
              alt="Maison Eze" 
              className="h-10 w-auto"
            />
          </a>
          <a 
            href="/"
            className="text-sm uppercase tracking-wider hover:opacity-70 transition-opacity"
            style={{ color: '#333333', letterSpacing: '0.15em' }}
          >
            Back to Home
          </a>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_elite-properties-18/artifacts/69sbmjqc_MaisonEze.png" 
              alt="Maison Eze" 
              className="h-16 w-auto mx-auto mb-6"
            />
            <h1 
              className="text-3xl md:text-4xl mb-4"
              style={{ 
                fontWeight: 300, 
                color: '#333333',
                letterSpacing: '-0.02em'
              }}
            >
              Property Search Mandate
            </h1>
            <p style={{ color: '#666666', lineHeight: 1.7, maxWidth: '600px', margin: '0 auto' }}>
              Fill out this form to initiate a tailored property search with Maison Eze. 
              All fields marked with <span style={{ color: '#C9A961' }}>*</span> are mandatory.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Section 1: Property Requirements */}
            <section 
              className="p-8"
              style={{ 
                background: '#f6f5e8', 
                border: '1px solid rgba(51,51,51,0.1)'
              }}
            >
              <h2 
                className="text-xl mb-6 pb-4"
                style={{ 
                  fontWeight: 500, 
                  color: '#333333',
                  borderBottom: '1px solid rgba(201,169,97,0.3)'
                }}
              >
                1. Property Requirements
              </h2>

              <div className="space-y-5">
                <div>
                  <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                    Locations (cities/regions) <span style={{ color: '#C9A961' }}>*</span>
                  </label>
                  <textarea
                    name="locations"
                    value={formData.locations}
                    onChange={handleChange}
                    maxLength={200}
                    placeholder="e.g., French Riviera, Provence, Athens"
                    className="w-full p-3 text-sm transition-all duration-200"
                    style={{ 
                      background: '#fffef2',
                      border: errors.locations ? '1px solid #e53e3e' : '1px solid rgba(51,51,51,0.2)',
                      outline: 'none',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                    onBlur={(e) => e.target.style.borderColor = errors.locations ? '#e53e3e' : 'rgba(51,51,51,0.2)'}
                  />
                  <div className="flex justify-between mt-1">
                    {errors.locations && <span className="text-xs" style={{ color: '#e53e3e' }}>{errors.locations}</span>}
                    <span className="text-xs ml-auto" style={{ color: '#999' }}>{formData.locations.length}/200</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                      Maximum Budget <span style={{ color: '#C9A961' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      placeholder="e.g., €500,000 or $1M"
                      className="w-full p-3 text-sm transition-all duration-200"
                      style={{ 
                        background: '#fffef2',
                        border: errors.budget ? '1px solid #e53e3e' : '1px solid rgba(51,51,51,0.2)',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                      onBlur={(e) => e.target.style.borderColor = errors.budget ? '#e53e3e' : 'rgba(51,51,51,0.2)'}
                    />
                    {errors.budget && <span className="text-xs mt-1 block" style={{ color: '#e53e3e' }}>{errors.budget}</span>}
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                      Minimum Space (m²/ft²) <span style={{ color: '#C9A961' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="minSpace"
                      value={formData.minSpace}
                      onChange={handleChange}
                      placeholder="e.g., 100m²"
                      className="w-full p-3 text-sm transition-all duration-200"
                      style={{ 
                        background: '#fffef2',
                        border: errors.minSpace ? '1px solid #e53e3e' : '1px solid rgba(51,51,51,0.2)',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                      onBlur={(e) => e.target.style.borderColor = errors.minSpace ? '#e53e3e' : 'rgba(51,51,51,0.2)'}
                    />
                    {errors.minSpace && <span className="text-xs mt-1 block" style={{ color: '#e53e3e' }}>{errors.minSpace}</span>}
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                    Property Type <span style={{ color: '#C9A961' }}>*</span>
                  </label>
                  <textarea
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleChange}
                    maxLength={200}
                    placeholder="e.g., Office, Warehouse, Land, Villa"
                    className="w-full p-3 text-sm transition-all duration-200"
                    style={{ 
                      background: '#fffef2',
                      border: errors.propertyType ? '1px solid #e53e3e' : '1px solid rgba(51,51,51,0.2)',
                      outline: 'none',
                      resize: 'vertical',
                      minHeight: '60px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                    onBlur={(e) => e.target.style.borderColor = errors.propertyType ? '#e53e3e' : 'rgba(51,51,51,0.2)'}
                  />
                  <div className="flex justify-between mt-1">
                    {errors.propertyType && <span className="text-xs" style={{ color: '#e53e3e' }}>{errors.propertyType}</span>}
                    <span className="text-xs ml-auto" style={{ color: '#999' }}>{formData.propertyType.length}/200</span>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                    Additional Notes
                  </label>
                  <textarea
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleChange}
                    maxLength={200}
                    placeholder="e.g., Must have parking, sea view, etc."
                    className="w-full p-3 text-sm transition-all duration-200"
                    style={{ 
                      background: '#fffef2',
                      border: '1px solid rgba(51,51,51,0.2)',
                      outline: 'none',
                      resize: 'vertical',
                      minHeight: '60px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(51,51,51,0.2)'}
                  />
                  <span className="text-xs mt-1 block text-right" style={{ color: '#999' }}>{formData.additionalNotes.length}/200</span>
                </div>
              </div>
            </section>

            {/* Section 2: Mandate Terms */}
            <section 
              className="p-8"
              style={{ 
                background: '#f6f5e8', 
                border: '1px solid rgba(51,51,51,0.1)'
              }}
            >
              <h2 
                className="text-xl mb-6 pb-4"
                style={{ 
                  fontWeight: 500, 
                  color: '#333333',
                  borderBottom: '1px solid rgba(201,169,97,0.3)'
                }}
              >
                2. Mandate Terms
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block mb-3 text-sm font-medium" style={{ color: '#333333' }}>
                    Exclusivity
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="radio"
                        name="exclusivity"
                        value="exclusive"
                        checked={formData.exclusivity === 'exclusive'}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <span 
                        className="w-5 h-5 border-2 rounded-full mr-3 flex items-center justify-center transition-all"
                        style={{ 
                          borderColor: formData.exclusivity === 'exclusive' ? '#C9A961' : 'rgba(51,51,51,0.3)',
                          background: formData.exclusivity === 'exclusive' ? '#C9A961' : 'transparent'
                        }}
                      >
                        {formData.exclusivity === 'exclusive' && (
                          <span className="w-2 h-2 rounded-full bg-white"></span>
                        )}
                      </span>
                      <span className="text-sm" style={{ color: '#333333' }}>Exclusive (no other agents)</span>
                    </label>
                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="radio"
                        name="exclusivity"
                        value="non-exclusive"
                        checked={formData.exclusivity === 'non-exclusive'}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <span 
                        className="w-5 h-5 border-2 rounded-full mr-3 flex items-center justify-center transition-all"
                        style={{ 
                          borderColor: formData.exclusivity === 'non-exclusive' ? '#C9A961' : 'rgba(51,51,51,0.3)',
                          background: formData.exclusivity === 'non-exclusive' ? '#C9A961' : 'transparent'
                        }}
                      >
                        {formData.exclusivity === 'non-exclusive' && (
                          <span className="w-2 h-2 rounded-full bg-white"></span>
                        )}
                      </span>
                      <span className="text-sm" style={{ color: '#333333' }}>Non-exclusive</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                      Duration <span style={{ color: '#C9A961' }}>*</span>
                    </label>
                    <select
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      className="w-full p-3 text-sm transition-all duration-200 cursor-pointer"
                      style={{ 
                        background: '#fffef2',
                        border: '1px solid rgba(51,51,51,0.2)',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(51,51,51,0.2)'}
                    >
                      <option value="3">3 months</option>
                      <option value="6">6 months</option>
                      <option value="12">12 months</option>
                      <option value="24">24 months</option>
                    </select>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                      Fees <span style={{ color: '#C9A961' }}>*</span>
                    </label>
                    <select
                      name="fees"
                      value={formData.fees}
                      onChange={handleChange}
                      className="w-full p-3 text-sm transition-all duration-200 cursor-pointer"
                      style={{ 
                        background: '#fffef2',
                        border: '1px solid rgba(51,51,51,0.2)',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(51,51,51,0.2)'}
                    >
                      <option value="5">5% of transaction</option>
                      <option value="negotiable">Negotiable (case-by-case)</option>
                    </select>
                  </div>
                </div>

                <p className="text-xs" style={{ color: '#666666' }}>
                  <em>Fees are due only upon successful transaction via Maison Eze.</em>
                </p>
              </div>
            </section>

            {/* Section 3: Contact Details */}
            <section 
              className="p-8"
              style={{ 
                background: '#f6f5e8', 
                border: '1px solid rgba(51,51,51,0.1)'
              }}
            >
              <h2 
                className="text-xl mb-6 pb-4"
                style={{ 
                  fontWeight: 500, 
                  color: '#333333',
                  borderBottom: '1px solid rgba(201,169,97,0.3)'
                }}
              >
                3. Contact Details
              </h2>

              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                      Full Name <span style={{ color: '#C9A961' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="clientName"
                      value={formData.clientName}
                      onChange={handleChange}
                      className="w-full p-3 text-sm transition-all duration-200"
                      style={{ 
                        background: '#fffef2',
                        border: errors.clientName ? '1px solid #e53e3e' : '1px solid rgba(51,51,51,0.2)',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                      onBlur={(e) => e.target.style.borderColor = errors.clientName ? '#e53e3e' : 'rgba(51,51,51,0.2)'}
                    />
                    {errors.clientName && <span className="text-xs mt-1 block" style={{ color: '#e53e3e' }}>{errors.clientName}</span>}
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                      Company (if applicable)
                    </label>
                    <input
                      type="text"
                      name="clientCompany"
                      value={formData.clientCompany}
                      onChange={handleChange}
                      className="w-full p-3 text-sm transition-all duration-200"
                      style={{ 
                        background: '#fffef2',
                        border: '1px solid rgba(51,51,51,0.2)',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(51,51,51,0.2)'}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                      Email <span style={{ color: '#C9A961' }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="clientEmail"
                      value={formData.clientEmail}
                      onChange={handleChange}
                      className="w-full p-3 text-sm transition-all duration-200"
                      style={{ 
                        background: '#fffef2',
                        border: errors.clientEmail ? '1px solid #e53e3e' : '1px solid rgba(51,51,51,0.2)',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                      onBlur={(e) => e.target.style.borderColor = errors.clientEmail ? '#e53e3e' : 'rgba(51,51,51,0.2)'}
                    />
                    {errors.clientEmail && <span className="text-xs mt-1 block" style={{ color: '#e53e3e' }}>{errors.clientEmail}</span>}
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: '#333333' }}>
                      Phone <span style={{ color: '#C9A961' }}>*</span>
                    </label>
                    <input
                      type="tel"
                      name="clientPhone"
                      value={formData.clientPhone}
                      onChange={handleChange}
                      className="w-full p-3 text-sm transition-all duration-200"
                      style={{ 
                        background: '#fffef2',
                        border: errors.clientPhone ? '1px solid #e53e3e' : '1px solid rgba(51,51,51,0.2)',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#C9A961'}
                      onBlur={(e) => e.target.style.borderColor = errors.clientPhone ? '#e53e3e' : 'rgba(51,51,51,0.2)'}
                    />
                    {errors.clientPhone && <span className="text-xs mt-1 block" style={{ color: '#e53e3e' }}>{errors.clientPhone}</span>}
                  </div>
                </div>

                {/* Maison Eze Representative (Pre-filled) */}
                <div 
                  className="mt-8 p-6"
                  style={{ 
                    background: 'rgba(201,169,97,0.08)',
                    border: '1px solid rgba(201,169,97,0.2)'
                  }}
                >
                  <div className="flex items-start gap-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_elite-properties-18/artifacts/69sbmjqc_MaisonEze.png" 
                      alt="Maison Eze" 
                      className="h-12 w-auto"
                    />
                    <div>
                      <h3 className="text-sm font-medium mb-3" style={{ color: '#C9A961', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                        Maison Eze Representative
                      </h3>
                      <p className="text-sm mb-1" style={{ color: '#333333' }}>
                        <strong>Name:</strong> Gregory Ezeguelian
                      </p>
                      <p className="text-sm mb-1" style={{ color: '#333333' }}>
                        <strong>Email:</strong> gregory@maison-eze.com
                      </p>
                      <p className="text-sm" style={{ color: '#333333' }}>
                        <strong>Phone:</strong> France +33 6 21371677 / Greece +30 699 3378555
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Hidden date field */}
            <input type="hidden" name="mandateDate" value={formData.mandateDate} />

            {/* Submit Button */}
            <div className="text-center pt-4">
              <button
                type="submit"
                className="px-12 py-4 text-sm uppercase tracking-wider transition-all duration-300 hover:shadow-lg"
                style={{ 
                  background: '#C9A961',
                  color: '#fffef2',
                  border: 'none',
                  letterSpacing: '0.15em',
                  fontWeight: 500
                }}
              >
                Review & Generate PDF
              </button>
            </div>
          </form>
        </div>
      </main>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)' }}
        >
          <div 
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8"
            style={{ background: '#fffef2' }}
          >
            <h2 
              className="text-2xl mb-2 text-center"
              style={{ fontWeight: 300, color: '#333333' }}
            >
              Review Your Mandate
            </h2>
            <p className="text-center mb-6 text-sm" style={{ color: '#666666' }}>
              Please verify the details below before confirming:
            </p>

            <div className="space-y-4 mb-8">
              <div className="p-4" style={{ background: '#f6f5e8', border: '1px solid rgba(51,51,51,0.1)' }}>
                <h4 className="text-sm font-medium mb-2" style={{ color: '#C9A961' }}>Property Requirements</h4>
                <p className="text-sm" style={{ color: '#333333' }}><strong>Locations:</strong> {formData.locations}</p>
                <p className="text-sm" style={{ color: '#333333' }}><strong>Budget:</strong> {formData.budget}</p>
                <p className="text-sm" style={{ color: '#333333' }}><strong>Min Space:</strong> {formData.minSpace}</p>
                <p className="text-sm" style={{ color: '#333333' }}><strong>Type:</strong> {formData.propertyType}</p>
                {formData.additionalNotes && <p className="text-sm" style={{ color: '#333333' }}><strong>Notes:</strong> {formData.additionalNotes}</p>}
              </div>

              <div className="p-4" style={{ background: '#f6f5e8', border: '1px solid rgba(51,51,51,0.1)' }}>
                <h4 className="text-sm font-medium mb-2" style={{ color: '#C9A961' }}>Mandate Terms</h4>
                <p className="text-sm" style={{ color: '#333333' }}><strong>Exclusivity:</strong> {formData.exclusivity === 'exclusive' ? 'Exclusive' : 'Non-exclusive'}</p>
                <p className="text-sm" style={{ color: '#333333' }}><strong>Duration:</strong> {formData.duration} months</p>
                <p className="text-sm" style={{ color: '#333333' }}><strong>Fees:</strong> {getFeesLabel(formData.fees)}</p>
              </div>

              <div className="p-4" style={{ background: '#f6f5e8', border: '1px solid rgba(51,51,51,0.1)' }}>
                <h4 className="text-sm font-medium mb-2" style={{ color: '#C9A961' }}>Your Details</h4>
                <p className="text-sm" style={{ color: '#333333' }}><strong>Name:</strong> {formData.clientName}</p>
                {formData.clientCompany && <p className="text-sm" style={{ color: '#333333' }}><strong>Company:</strong> {formData.clientCompany}</p>}
                <p className="text-sm" style={{ color: '#333333' }}><strong>Email:</strong> {formData.clientEmail}</p>
                <p className="text-sm" style={{ color: '#333333' }}><strong>Phone:</strong> {formData.clientPhone}</p>
              </div>

              <p className="text-xs text-center" style={{ color: '#666666' }}>
                <strong>Date:</strong> {formData.mandateDate}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleConfirm}
                className="px-8 py-3 text-sm uppercase tracking-wider transition-all duration-300"
                style={{ 
                  background: '#C9A961',
                  color: '#fffef2',
                  border: 'none',
                  letterSpacing: '0.1em',
                  fontWeight: 500
                }}
              >
                Confirm & Download PDF
              </button>
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-8 py-3 text-sm uppercase tracking-wider transition-all duration-300"
                style={{ 
                  background: 'transparent',
                  color: '#333333',
                  border: '1px solid rgba(51,51,51,0.3)',
                  letterSpacing: '0.1em',
                  fontWeight: 500
                }}
              >
                Modify
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 text-center" style={{ borderTop: '1px solid rgba(51,51,51,0.1)' }}>
        <p className="text-xs" style={{ color: '#666666' }}>
          &copy; {new Date().getFullYear()} Maison Eze | contact@maison-eze.com
        </p>
      </footer>
    </div>
  );
};

export default MandateForm;
